var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, styled } from '@mui/material';
/**
 * The markdown the assistant answers in: paragraphs, headings, lists, tables, code, bold and
 * italics. Built from React elements, never from HTML, so nothing a model writes can inject
 * markup; links render as their text, since the assistant opens things through buttons.
 */
var Code = styled('code')(function (_a) {
  var theme = _a.theme;
  return {
    fontFamily: 'monospace',
    backgroundColor: theme.palette.grey[100],
    borderRadius: 2,
    padding: '0 2px'
  };
});
var Pre = styled('pre')(function (_a) {
  var theme = _a.theme;
  return {
    margin: 0,
    padding: theme.spacing(2),
    overflowX: 'auto',
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontSize: '0.8rem'
  };
});
var List = styled('ul')({
  margin: 0,
  paddingLeft: '1.25rem'
});
/**
 * The inline spans, each matched only where it starts (sticky) and rendered from its body, keyed
 * by where it starts. No body runs past the next of its own marks, so the scan stays linear.
 */
var SPANS = [{
  pattern: /`([^`]+)`/y,
  render: function (body, key) {
    return _jsx(Code, {
      children: body
    }, key);
  }
}, {
  pattern: /\*\*([^*]+)\*\*/y,
  render: function (body, key) {
    return _jsx("strong", {
      children: body
    }, key);
  }
}, {
  pattern: /__([^_]+)__/y,
  render: function (body, key) {
    return _jsx("strong", {
      children: body
    }, key);
  }
}, {
  pattern: /\*([^*\s][^*]*)\*/y,
  render: function (body, key) {
    return _jsx("em", {
      children: body
    }, key);
  }
}, {
  pattern: /\[([^[\]]+)\]\([^)[]*\)/y,
  render: function (body) {
    return body;
  }
}];
var spanAt = function (text, at) {
  for (var _i = 0, SPANS_1 = SPANS; _i < SPANS_1.length; _i++) {
    var _a = SPANS_1[_i],
      pattern = _a.pattern,
      render = _a.render;
    pattern.lastIndex = at;
    var match = pattern.exec(text);
    if (match) {
      return {
        node: render(match[1], at),
        end: pattern.lastIndex
      };
    }
  }
  return undefined;
};
export function renderInline(text) {
  var nodes = [];
  var plainFrom = 0;
  var at = 0;
  while (at < text.length) {
    var span = spanAt(text, at);
    if (span) {
      if (at > plainFrom) {
        nodes.push(text.slice(plainFrom, at));
      }
      nodes.push(span.node);
      at = span.end;
      plainFrom = at;
    } else {
      at += 1;
    }
  }
  if (plainFrom < text.length) {
    nodes.push(text.slice(plainFrom));
  }
  return nodes;
}
var BULLET = /^\s*[-*•]\s+/;
var NUMBERED = /^\s*\d+[.)]\s+/;
var HEADING = /^#{1,6}\s+/;
var TABLE_ROW = /^\s*\|/;
var TABLE_RULE = /^\s*(?:\|\s*)?:?-{2,}/;
/** A table row's cells, each with its column, which is what identifies a cell within its row. */
var cellsOf = function (row) {
  return row.trim().replace(/^\||\|$/g, '').split('|').map(function (cell, column) {
    return {
      column: column,
      text: cell.trim()
    };
  });
};
var TableCells = function (_a) {
  var row = _a.row;
  return _jsx(_Fragment, {
    children: cellsOf(row).map(function (_a) {
      var column = _a.column,
        text = _a.text;
      return _jsx(TableCell, {
        children: renderInline(text)
      }, column);
    })
  });
};
export var Markdown = function (_a) {
  var text = _a.text;
  var lines = text.replace(/\r/g, '').split('\n');
  var blocks = [];
  var i = 0;
  /** Takes the lines from here on that match, as their line numbers, which key what they render. */
  var take = function (matches) {
    var taken = [];
    while (i < lines.length && matches(lines[i])) {
      taken.push(i);
      i += 1;
    }
    return taken;
  };
  var join = function (taken, separator) {
    return taken.map(function (n) {
      return lines[n];
    }).join(separator);
  };
  var _loop_1 = function () {
    var line = lines[i];
    var key = i;
    if (!line.trim()) {
      i += 1;
    } else if (line.trim().startsWith('```')) {
      i += 1;
      var code = take(function (l) {
        return !l.trim().startsWith('```');
      });
      i += 1;
      blocks.push(_jsx(Pre, {
        children: join(code, '\n')
      }, key));
    } else if (HEADING.test(line)) {
      i += 1;
      blocks.push(_jsx(Typography, __assign({
        variant: "subtitle2",
        fontWeight: "bold"
      }, {
        children: renderInline(line.replace(HEADING, ''))
      }), key));
    } else if (TABLE_ROW.test(line)) {
      var _b = take(function (l) {
          return TABLE_ROW.test(l);
        }).filter(function (n) {
          return !TABLE_RULE.test(lines[n]);
        }),
        header = _b[0],
        body = _b.slice(1);
      blocks.push(_jsx(Box, __assign({
        sx: {
          overflowX: 'auto'
        }
      }, {
        children: _jsxs(Table, __assign({
          size: "small"
        }, {
          children: [_jsx(TableHead, {
            children: _jsx(TableRow, {
              children: header !== undefined && _jsx(TableCells, {
                row: lines[header]
              })
            })
          }), _jsx(TableBody, {
            children: body.map(function (n) {
              return _jsx(TableRow, {
                children: _jsx(TableCells, {
                  row: lines[n]
                })
              }, n);
            })
          })]
        }))
      }), key));
    } else if (BULLET.test(line) || NUMBERED.test(line)) {
      var numbered = NUMBERED.test(line);
      var marker_1 = numbered ? NUMBERED : BULLET;
      var items = take(function (l) {
        return marker_1.test(l);
      });
      blocks.push(_jsx(List, __assign({
        as: numbered ? 'ol' : 'ul'
      }, {
        children: items.map(function (n) {
          return _jsx("li", {
            children: _jsx(Typography, __assign({
              variant: "body2"
            }, {
              children: renderInline(lines[n].replace(marker_1, ''))
            }))
          }, n);
        })
      }), key));
    } else {
      var paragraph = take(function (l) {
        return Boolean(l.trim()) && !HEADING.test(l) && !TABLE_ROW.test(l) && !BULLET.test(l) && !NUMBERED.test(l) && !l.trim().startsWith('```');
      });
      blocks.push(_jsx(Typography, __assign({
        variant: "body2"
      }, {
        children: renderInline(join(paragraph, ' '))
      }), key));
    }
  };
  while (i < lines.length) {
    _loop_1();
  }
  return _jsx(Box, __assign({
    sx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }
  }, {
    children: blocks
  }));
};